import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { catalogRpc, transformCatalogProgram } from '../api';

interface UseCatalogOptions {
  accessTier?: 'free' | 'power_rack';
}

export function useCatalog(options: UseCatalogOptions = {}) {
  const { accessTier } = options;

  const query = useQuery({
    queryKey: ['admin', 'catalog', accessTier],
    queryFn: async () => {
      const { data, error } = await catalogRpc.getCatalogPrograms({
        p_access_tier: accessTier,
      });

      if (error) throw error;
      if (!data) throw new Error('No data returned');

      return data.map(transformCatalogProgram);
    },
  });

  return {
    programs: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useToggleFeatured() {
  const queryClient = useQueryClient();
  const [isToggling, setIsToggling] = useState(false);

  const toggleFeatured = useCallback(
    async (programId: string, isFeatured: boolean) => {
      setIsToggling(true);
      try {
        const { error } = await catalogRpc.toggleFeatured({
          p_program_id: programId,
          p_is_featured: isFeatured,
        });

        if (error) throw error;

        await queryClient.invalidateQueries({ queryKey: ['admin', 'catalog'] });
        return { success: true };
      } catch (err) {
        return { success: false, error: err as Error };
      } finally {
        setIsToggling(false);
      }
    },
    [queryClient]
  );

  return { toggleFeatured, isToggling };
}

export function useDeleteCatalogProgram() {
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);

  const deleteProgram = useCallback(
    async (programId: string) => {
      setIsDeleting(true);
      try {
        const { error } = await catalogRpc.deleteCatalogProgram({
          p_program_id: programId,
        });

        if (error) throw error;

        await queryClient.invalidateQueries({ queryKey: ['admin', 'catalog'] });
        return { success: true };
      } catch (err) {
        return { success: false, error: err as Error };
      } finally {
        setIsDeleting(false);
      }
    },
    [queryClient]
  );

  return { deleteProgram, isDeleting };
}
